import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { useSplitBillAnalytics } from "@dineout-payment-success-ui/hooks/useSplitBillAnalytics";
import { roundMoney2 } from "@dineout-payment-success-ui/utils/splitBillMoney";

import {
    Contact,
    GuestSplitsMap,
    IDineCashSplit,
    ISplitBillDineCashOptions,
    ISplitModeOption,
    SplitBillConfirmData,
    SplitMethod,
} from "../../../../interfaces";
import { MIN_SHARE_VALUE, SplitBillStep } from "../../../../constants";
import {
    calculateEachPersonShare,
    calculateEqualSplits,
    convertSplitsToActualAmounts,
    extractSplitsByMethod,
    validateSplits,
} from "./splitBillHelpers";

interface UseSplitBillHandlersParams {
    billAmount: number;
    splitModeOptions: ISplitModeOption[];
    dineCashSplit?: IDineCashSplit;
    orderedContacts: Contact[];
    onConfirm: (splitData: SplitBillConfirmData, upiId: string) => void;
    handleClose: () => void;
    onAddMoreGuests: (onSuccess?: (newCount: number) => void) => void;
    orderId: string;
    restaurantId: string;
    splitBillError: string;
    splitBillSuccess: boolean;
    guestSplits: GuestSplitsMap;
    setGuestSplits: Dispatch<SetStateAction<GuestSplitsMap>>;
    selectedTab: SplitMethod;
    setSelectedTab: Dispatch<SetStateAction<SplitMethod>>;
    setValidationError: Dispatch<SetStateAction<string>>;
    setCurrentStep: Dispatch<SetStateAction<SplitBillStep>>;
}

const getInitialDineCashOptions = (dineCashSplit?: IDineCashSplit): ISplitBillDineCashOptions => ({
    dineCashAmount: String(Number(dineCashSplit?.dineCashAmount || 0)),
    splitDineCash: dineCashSplit?.splitDineCash ?? false,
});

export const useSplitBillHandlers = ({
    billAmount,
    splitModeOptions,
    dineCashSplit,
    orderedContacts,
    onConfirm,
    handleClose,
    onAddMoreGuests,
    orderId,
    restaurantId,
    splitBillError,
    splitBillSuccess,
    guestSplits,
    setGuestSplits,
    selectedTab,
    setSelectedTab,
    setValidationError,
    setCurrentStep,
}: UseSplitBillHandlersParams) => {
    const [confirmedSplitData, setConfirmedSplitData] = useState<{
        method: SplitMethod;
        splits: Record<string, number>;
        dineCashAmount: string;
        splitDineCash: boolean;
    } | null>(null);
    const [dineCashOptions, setDineCashOptions] = useState<ISplitBillDineCashOptions>(() =>
        getInitialDineCashOptions(dineCashSplit),
    );
    const [tabsImpressed, setTabsImpressed] = useState(false);

    useEffect(() => {
        setDineCashOptions(getInitialDineCashOptions(dineCashSplit));
    }, [dineCashSplit?.dineCashAmount, dineCashSplit?.splitDineCash]);

    const {
        onImpressionSplitMethodTab,
        onClickSplitMethodTab,
        onClickSplitConfirmCta,
        onClickSplitEqually,
        onImpressionSplitError,
        onImpressionSplitSuccessWidget,
        onClickSplitDoneCta,
        onClickAddMoreGuest,
    } = useSplitBillAnalytics();

    const getBaseAnalyticsContext = useCallback(
        () => ({
            orderId,
            amountPaidByUser: billAmount,
            numberOfGuest: orderedContacts.length,
            restaurantId,
        }),
        [orderId, billAmount, orderedContacts, restaurantId],
    );

    useEffectOnce(() => {
        if (!tabsImpressed) {
            splitModeOptions.forEach((option, index) => {
                onImpressionSplitMethodTab({
                    ...getBaseAnalyticsContext(),
                    tabType: option.type,
                    position: index + 1,
                });
            });
            setTabsImpressed(true);
        }
    });

    useEffect(() => {
        if (splitBillError && confirmedSplitData) {
            onImpressionSplitError({
                bookingId: orderId,
                totalBillAmount: billAmount,
                splitMethod: confirmedSplitData.method,
                error: splitBillError,
                restaurantId,
            });
        }
    }, [splitBillError, confirmedSplitData, orderId, billAmount, restaurantId, onImpressionSplitError]);

    useEffect(() => {
        if (splitBillSuccess && confirmedSplitData) {
            onImpressionSplitSuccessWidget({
                bookingId: orderId,
                totalBillAmount: billAmount,
                splitMethod: confirmedSplitData.method,
                restaurantId,
            });
        }
    }, [splitBillSuccess, confirmedSplitData, onImpressionSplitSuccessWidget, orderId, billAmount, restaurantId]);

    const handleTabChange = useCallback(
        (tab: SplitMethod) => {
            const tabPosition = splitModeOptions.findIndex((opt) => opt.type === tab) + 1;
            onClickSplitMethodTab({
                ...getBaseAnalyticsContext(),
                selectedTab: tab,
                tabPosition,
            });
            setSelectedTab(tab);
            setValidationError("");
        },
        [splitModeOptions, getBaseAnalyticsContext, onClickSplitMethodTab, setSelectedTab, setValidationError],
    );

    const handleSplitEqually = useCallback(() => {
        onClickSplitEqually({
            ...getBaseAnalyticsContext(),
            splitMethod: selectedTab,
        });
        const newSplits = calculateEqualSplits(guestSplits, orderedContacts, selectedTab, billAmount);
        setGuestSplits(newSplits);
        setValidationError("");
    }, [onClickSplitEqually, getBaseAnalyticsContext, guestSplits, orderedContacts, selectedTab, billAmount, setGuestSplits, setValidationError]);

    const handlePercentageChange = useCallback(
        (phoneNumber: string, value: string) => {
            const numValue = roundMoney2(parseFloat(value) || 0);
            setGuestSplits((prev) => {
                if (!prev[phoneNumber]) return prev;
                return { ...prev, [phoneNumber]: { ...prev[phoneNumber], percentage: numValue } };
            });
        },
        [setGuestSplits],
    );

    const handleAmountChange = useCallback(
        (phoneNumber: string, value: string) => {
            const numValue = roundMoney2(parseFloat(value) || 0);
            setGuestSplits((prev) => {
                if (!prev[phoneNumber]) return prev;
                return { ...prev, [phoneNumber]: { ...prev[phoneNumber], amount: numValue } };
            });
        },
        [setGuestSplits],
    );

    const handleInputBlur = useCallback(() => {
        const validation = validateSplits(guestSplits, orderedContacts, selectedTab, billAmount);
        if (!validation.isValid && validation.message) {
            setValidationError(validation.message);
        } else {
            setValidationError("");
        }
    }, [guestSplits, orderedContacts, selectedTab, billAmount, setValidationError]);

    const handleShareIncrement = useCallback(
        (phoneNumber: string) => {
            setGuestSplits((prev) => {
                if (!prev[phoneNumber]) return prev;
                return { ...prev, [phoneNumber]: { ...prev[phoneNumber], share: prev[phoneNumber].share + 1 } };
            });
        },
        [setGuestSplits],
    );

    const handleShareDecrement = useCallback(
        (phoneNumber: string) => {
            setGuestSplits((prev) => {
                if (!prev[phoneNumber]) return prev;
                return {
                    ...prev,
                    [phoneNumber]: {
                        ...prev[phoneNumber],
                        share: Math.max(MIN_SHARE_VALUE, prev[phoneNumber].share - 1),
                    },
                };
            });
        },
        [setGuestSplits],
    );

    const isFormValid = useMemo(
        () => validateSplits(guestSplits, orderedContacts, selectedTab, billAmount).isValid,
        [guestSplits, orderedContacts, selectedTab, billAmount],
    );

    const handleConfirm = useCallback(() => {
        const validation = validateSplits(guestSplits, orderedContacts, selectedTab, billAmount);
        if (!validation.isValid && validation.message) {
            setValidationError(validation.message);
            return;
        }

        const splits = extractSplitsByMethod(guestSplits, orderedContacts, selectedTab);
        const eachPersonShare = calculateEachPersonShare(splits, selectedTab, orderedContacts, billAmount);
        onClickSplitConfirmCta({
            ...getBaseAnalyticsContext(),
            splitMethod: selectedTab,
            eachPersonShare,
        });

        setConfirmedSplitData({
            method: selectedTab,
            splits,
            dineCashAmount: dineCashOptions.dineCashAmount,
            splitDineCash: dineCashOptions.splitDineCash,
        });
        setCurrentStep(SplitBillStep.UPI_ENTRY);
    }, [
        guestSplits,
        orderedContacts,
        selectedTab,
        billAmount,
        dineCashOptions,
        getBaseAnalyticsContext,
        onClickSplitConfirmCta,
        setValidationError,
        setCurrentStep,
    ]);

    const handleSendRequest = useCallback(
        (upiId: string) => {
            if (isEmpty(confirmedSplitData)) return;

            const actualSplits = convertSplitsToActualAmounts(
                confirmedSplitData.splits,
                confirmedSplitData.method,
                orderedContacts,
                billAmount,
            );

            onConfirm(
                {
                    method: confirmedSplitData.method,
                    splits: actualSplits,
                    billAmount,
                    dineCashAmount: confirmedSplitData.dineCashAmount,
                    splitDineCash: confirmedSplitData.splitDineCash,
                },
                upiId,
            );
        },
        [confirmedSplitData, orderedContacts, billAmount, onConfirm],
    );

    const handleSuccessDone = useCallback(() => {
        if (confirmedSplitData) {
            onClickSplitDoneCta({
                bookingId: orderId,
                totalBillAmount: billAmount,
                splitMethod: confirmedSplitData.method,
                restaurantId,
            });
        }
        handleClose();
    }, [confirmedSplitData, orderId, billAmount, restaurantId, handleClose, onClickSplitDoneCta]);

    const handleAddMoreGuests = useCallback(() => {
        onAddMoreGuests((newCount) => {
            onClickAddMoreGuest({
                orderId,
                amountPaidByUser: billAmount,
                numberOfGuest: newCount,
                splitMethod: selectedTab,
                restaurantId,
            });
        });
    }, [onAddMoreGuests, onClickAddMoreGuest, orderId, billAmount, selectedTab, restaurantId]);

    return {
        confirmedSplitData,
        isFormValid,
        handleTabChange,
        handleSplitEqually,
        handlePercentageChange,
        handleAmountChange,
        handleInputBlur,
        handleShareIncrement,
        handleShareDecrement,
        handleConfirm,
        handleDineCashSelectionChange: setDineCashOptions,
        handleSendRequest,
        handleSuccessDone,
        handleAddMoreGuests,
    };
};
